import { hexToHsl } from './color';

type Rgb = [number, number, number];

const normalizeHex = (hex: string) => {
  const value = hex.trim().replace('#', '');
  if (/^[0-9a-fA-F]{6}$/.test(value)) return value;
  if (/^[0-9a-fA-F]{3}$/.test(value)) {
    return value
      .split('')
      .map((char) => char + char)
      .join('');
  }
  return null;
};

const hexToRgb = (hex: string): Rgb | null => {
  const value = normalizeHex(hex);
  if (!value) return null;
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
};

/** Splits the "h s% l%" string returned by hexToHsl into numbers. */
const parseHsl = (hsl: string) => {
  const match = /^(\d+) (\d+)% (\d+)%$/.exec(hsl);
  if (!match) return null;
  return { h: Number(match[1]), s: Number(match[2]), l: Number(match[3]) };
};

const hslToRgb = (h: number, s: number, l: number): Rgb => {
  const sat = s / 100;
  const lig = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sat * Math.min(lig, 1 - lig);
  const f = (n: number) => lig - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0), f(8), f(4)];
};

const relativeLuminance = ([r, g, b]: Rgb) => {
  const linear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
};

/** True when the color is dark enough to need light text on top of it. */
export const isColorDark = (hex: string): boolean => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const [r, g, b] = rgb;
  return 0.299 * r + 0.587 * g + 0.114 * b < 140;
};

/**
 * The brand hue darkened just enough to read as text on white (WCAG AA, 4.5:1).
 * Returns an "h s% l%" string for a CSS custom property, or null for an invalid color.
 */
export const brandTextHsl = (hex: string): string | null => {
  const hsl = parseHsl(hexToHsl(hex) ?? '');
  if (!hsl) return null;

  let lightness = hsl.l;
  while (
    lightness > 0 &&
    1.05 / (relativeLuminance(hslToRgb(hsl.h, hsl.s, lightness)) + 0.05) < 4.5
  ) {
    lightness -= 1;
  }

  return `${hsl.h} ${hsl.s}% ${lightness}%`;
};
